import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  FormHelperText,
  CircularProgress,
  Grid,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import * as Yup from "yup";
import { createBusinessLoanRequest } from "../../../services/customer.service";
import { commonMessage } from "../../../constants/message.constant";
import { DealType, LoanType } from "../../../validation/customer.validation";
import { SnackbarSeverity } from "../../../constants/general.constant";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

interface Props {
  open: boolean;
  customerId: string;
  businessId: string;
  onClose: () => void;
  onSuccess?: (loan?: any) => void;
}

interface ProceedsRow {
  id: number;
  purpose: string;
  amount: string;
}

interface DealData {
  loanName: string;
  dealType: DealType;
  loanType: LoanType;
  loanSize: string;
  maxLTV: string;
  minDSCRatio: string;
  payoffValue: string;
  guaranteedName?: string;
}

const newDealSchema = Yup.object({
  loanName: Yup.string()
    .trim()
    .min(3, "Loan Name must be at least 3 characters")
    .required("Loan Name is required."),
  dealType: Yup.string()
    .oneOf(
      [DealType.Purchase, DealType.Refinance, DealType.Startup],
      "Please select a valid Deal Type.",
    )
    .required("Deal Type is required."),
  loanType: Yup.string()
    .oneOf([LoanType.Conventional, LoanType.SBA], "Please select a valid Loan Type.")
    .required("Loan Type is required."),
  loanSize: Yup.number()
    .typeError("Loan Size must be a number.")
    .min(1000, "Loan Size must be at least $1,000.")
    .required("Loan Size is required."),
  maxLTV: Yup.number()
    .typeError("Max LTV must be a number.")
    .min(0)
    .max(100, "Max LTV cannot exceed 100.")
    .required("Max LTV is required."),
  minDSCRatio: Yup.number()
    .typeError("Min DSC Ratio must be a number.")
    .min(0.5, "Min DSC Ratio should be 0.5 or greater.")
    .required("Min DSC Ratio is required."),
  payoffValue: Yup.string()
    .when("dealType", {
      is: DealType.Refinance,
      then: (schema) =>
        schema
          .required("Payoff Value is required for refinance deals.")
          .test("is-number", "Payoff Value must be a valid number.", (value) => !value || /^\d*\.?\d+$/.test(value))
          .test("is-positive", "Payoff Value must be a positive number.", (value) => !value || Number(value) > 0),
      otherwise: (schema) => schema.notRequired(),
    }),
}).test(
  "loan-sum",
  "Payoff Value + Proceeds cannot exceed Loan Size",
  function (value) {
    if (value.dealType !== DealType.Refinance) return true;
    if (!value.payoffValue || !value.loanSize) return true;

    const loanSize = Number(value.loanSize);
    const payoffValue = Number(value.payoffValue);
    if (Number.isNaN(loanSize) || Number.isNaN(payoffValue)) return true;

    const proceedsTotal = (this.options.context?.proceeds || [])
      .filter((p: ProceedsRow) => p.purpose?.trim() && p.amount?.trim())
      .reduce((sum: number, p: ProceedsRow) => sum + Number(p.amount), 0);

    const total = payoffValue + proceedsTotal;
    if (total > loanSize) {
      return this.createError({
        message: `Payoff Value + Proceeds ($ ${total.toLocaleString()}) cannot exceed Loan Size ($ ${loanSize.toLocaleString()})`,
        path: "loanSize",
      });
    }
    return true;
  }
);

const initialDealData: DealData = {
  loanName: "",
  dealType: "" as DealType,
  loanType: "" as LoanType,
  loanSize: "",
  maxLTV: "",
  minDSCRatio: "",
  payoffValue: "",
  guaranteedName: "",
};

const initialProceedsRow: ProceedsRow = {
  id: 1,
  purpose: "",
  amount: "",
};

const NewLoanDialog: React.FC<Props> = ({
  open,
  customerId,
  businessId,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [dealData, setDealData] = useState<DealData>(initialDealData);
  const [proceeds, setProceeds] = useState<ProceedsRow[]>([initialProceedsRow]);
  const [proceedsErrors, setProceedsErrors] = useState<Record<number, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: SnackbarSeverity.Success,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDealData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProceedsChange = (
    id: number,
    field: "purpose" | "amount",
    value: string,
  ) => {
    setProceeds((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
    setProceedsErrors((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: "" },
    }));
  };

  const addProceedsRow = () => {
    const newId = Math.max(...proceeds.map((p) => p.id), 0) + 1;
    setProceeds((prev) => [...prev, { id: newId, purpose: "", amount: "" }]);
    setProceedsErrors((prev) => ({ ...prev, [newId]: { purpose: "", amount: "" } }));
  };

  const removeProceedsRow = (id: number) => {
    if (proceeds.length > 1) {
      setProceeds((prev) => prev.filter((row) => row.id !== id));
      setProceedsErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "loanType") {
      const dscValue = value === LoanType.Conventional ? "1.25" : "1.10";
      setDealData((prev) => ({ ...prev, [name]: value, minDSCRatio: dscValue }));
      setFormErrors((prev) => ({ ...prev, [name]: "", minDSCRatio: "" }));
    } else if (name === "dealType") {
      const ltvValue = value === DealType.Refinance ? "75" : "80";
      setDealData((prev) => ({ ...prev, [name]: value, maxLTV: ltvValue }));
      setFormErrors((prev) => ({ ...prev, [name]: "", maxLTV: "" }));
    } else {
      setDealData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const showSnackbar = (message: string, severity: any) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCloseDialog = () => {
    setDealData(initialDealData);
    setProceeds([initialProceedsRow]);
    setProceedsErrors({});
    setFormErrors({});
    onClose();
  };

  const getError = (field: string) => formErrors[field];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setProceedsErrors({});
    setLoading(true);

    try {
      await newDealSchema.validate(dealData, { abortEarly: false, context: { proceeds } });

      if (dealData.dealType === DealType.Refinance && dealData.payoffValue && dealData.loanSize) {
        const newProceedsErrors: Record<number, Record<string, string>> = {};
        let hasProceedsErrors = false;

        proceeds.forEach((p) => {
          if (p.purpose.trim() && !p.amount.trim()) {
            newProceedsErrors[p.id] = { ...newProceedsErrors[p.id], amount: "Amount is required when purpose is specified." };
            hasProceedsErrors = true;
          }
          if (!p.purpose.trim() && p.amount.trim()) {
            newProceedsErrors[p.id] = { ...newProceedsErrors[p.id], purpose: "Purpose is required when amount is specified." };
            hasProceedsErrors = true;
          }
        });

        if (hasProceedsErrors) {
          setProceedsErrors(newProceedsErrors);
          setLoading(false);
          return;
        }
      }

      const proceedsList = proceeds
        .filter((p) => p.purpose.trim() !== "" && p.amount.trim() !== "")
        .map((p) => ({
          purpose: p.purpose.trim(),
          amount: Number(p.amount),
        }));

      const payload = {
        loanName: dealData.loanName.trim(),
        dealType: dealData.dealType,
        loanType: dealData.loanType,
        loanSize: Number(dealData.loanSize),
        maxLTV: Number(dealData.maxLTV),
        minDSCRatio: Number(dealData.minDSCRatio),
        payoffValue: dealData.payoffValue ? Number(dealData.payoffValue) : undefined,
        proceeds: proceedsList,
        ...(dealData.guaranteedName?.trim()
          ? { guaranteedName: dealData.guaranteedName.trim() }
          : {}),
      };

      const createdLoan = await createBusinessLoanRequest(businessId, payload);

      setDealData(initialDealData);
      setProceeds([initialProceedsRow]);
      setProceedsErrors({});
      setFormErrors({});

      showSnackbar("Loan request created successfully!", SnackbarSeverity.Success);

      setTimeout(() => {
        onClose();
        if (onSuccess) {
          onSuccess(createdLoan);
        }
      }, 1200);

    } catch (error: any) {
      setSnackbar({
        open: true,
        message:
          error?.errors?.message || error?.message ||
          error?.response?.data?.message ||
          commonMessage.unknownError,
        severity: SnackbarSeverity.Error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        sx={{
          "& .MuiDialog-paper": {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 64px)" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle sx={{ m: 0, p: { xs: 1.5, sm: 2 }, pr: 6 }}>
          <Typography variant="h6" fontWeight={600}>
            New Loan Request
          </Typography>

          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: "gray",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* CONTENT */}
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} mt={1}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Loan Name"
                    name="loanName"
                    value={dealData.loanName}
                    onChange={handleChange}
                    error={!!getError("loanName")}
                    helperText={getError("loanName")}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={!!getError("dealType")}>
                    <InputLabel>Deal Type</InputLabel>
                    <Select
                      name="dealType"
                      value={dealData.dealType}
                      onChange={handleSelectChange}
                      label="Deal Type"
                      sx={{ borderRadius: "10px" }}
                    >
                      <MenuItem value="purchase">Purchase</MenuItem>
                      <MenuItem value="refinance">Refinance</MenuItem>
                      <MenuItem value="startup">Startup</MenuItem>
                    </Select>
                    <FormHelperText>{getError("dealType")}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth error={!!getError("loanType")}>
                    <InputLabel>Loan Type</InputLabel>
                    <Select
                      name="loanType"
                      value={dealData.loanType}
                      onChange={handleSelectChange}
                      label="Loan Type"
                      sx={{ borderRadius: "10px" }}
                    >
                      <MenuItem value="conventional">Conventional</MenuItem>
                      <MenuItem value="SBA">SBA</MenuItem>
                    </Select>
                    <FormHelperText>{getError("loanType")}</FormHelperText>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Loan Size ($)"
                    name="loanSize"
                    value={dealData.loanSize}
                    onChange={handleChange}
                    error={!!getError("loanSize")}
                    helperText={getError("loanSize")}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max LTV (%)"
                    name="maxLTV"
                    value={dealData.maxLTV}
                    disabled
                    error={!!getError("maxLTV")}
                    helperText={getError("maxLTV")}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      "& .MuiOutlinedInput-root.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min DSC Ratio"
                    name="minDSCRatio"
                    value={dealData.minDSCRatio}
                    disabled
                    error={!!getError("minDSCRatio")}
                    helperText={getError("minDSCRatio")}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      "& .MuiOutlinedInput-root.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  />
                </Grid>

                {dealData.dealType === DealType.Refinance && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Payoff Value ($)"
                      name="payoffValue"
                      value={dealData.payoffValue}
                      onChange={handleChange}
                      error={!!getError("payoffValue")}
                      helperText={getError("payoffValue")}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, md: 12 }}>
                  <TextField
                    fullWidth
                    label="Guaranteed Name"
                    name="guaranteedName"
                    value={dealData.guaranteedName || ""}
                    onChange={handleChange}
                    error={!!getError("guaranteedName")}
                    helperText={getError("guaranteedName")}
                    placeholder="Optional"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Grid>

                {dealData.dealType === DealType.Refinance &&
                  dealData.payoffValue &&
                  Number(dealData.payoffValue) < Number(dealData.loanSize) &&
                  Number(dealData.loanSize) > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        Proceeds Table
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Purpose</TableCell>
                              <TableCell>Amount ($)</TableCell>
                              <TableCell align="right" sx={{ width: 60 }}>
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {proceeds.map((row) => (
                              <TableRow key={row.id}>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={row.purpose}
                                    onChange={(e) =>
                                      handleProceedsChange(
                                        row.id,
                                        "purpose",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter purpose"
                                    error={!!proceedsErrors[row.id]?.purpose}
                                    helperText={proceedsErrors[row.id]?.purpose}
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    value={row.amount}
                                    onChange={(e) =>
                                      handleProceedsChange(
                                        row.id,
                                        "amount",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter amount"
                                    error={!!proceedsErrors[row.id]?.amount}
                                    helperText={proceedsErrors[row.id]?.amount}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => removeProceedsRow(row.id)}
                                    disabled={proceeds.length === 1}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addProceedsRow}
                        sx={{ mt: 1 }}
                        size="small"
                      >
                        Add Row
                      </Button>
                    </Grid>
                  )}
              </Grid>
              {/* ACTIONS */}
              <br></br>

              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                loading={loading}
                loadingPosition="end"
                endIcon={<SendIcon />}
                className="mt-6 bg-blue-600 hover:bg-blue-700 transition duration-150 ease-in-out"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  style: { borderRadius: 10 },
                }}
              >
                {"Submit New Deal"}
              </LoadingButton>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewLoanDialog;
